import React from 'react';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { MapPin, Calendar, Clock, Mail, Phone, Instagram } from 'lucide-react';
import { getDayOfWeek } from './utils';

const DEFAULT_INSTAGRAM_URL = 'https://www.instagram.com/smashzone.bn/';

export default function FiltersCard({
  locations, selectedLocation, onLocationChange,
  selectedDate, setSelectedDate, minDate, maxDate,
  isLocationClosed, getOperatingHoursDisplay, getBreakInfo,
}) {
  const dayName = selectedDate
    ? getDayOfWeek(selectedDate).replace(/^./, (c) => c.toUpperCase())
    : '';

  return (
    <Card className="p-6 mb-8">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <Label className="uppercase text-xs font-bold text-muted-foreground mb-2 block">
            <MapPin className="w-4 h-4 inline mr-1" /> Location
          </Label>
          <Select value={selectedLocation?.id} onValueChange={onLocationChange}>
            <SelectTrigger data-testid="location-select" className="h-12 rounded-2xl">
              <SelectValue placeholder="Select a location" />
            </SelectTrigger>
            <SelectContent>
              {locations.map((loc) => (
                <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {selectedLocation && (
            <p className="text-xs text-muted-foreground mt-1">{selectedLocation.address}</p>
          )}
        </div>

        <div>
          <Label htmlFor="date" className="uppercase text-xs font-bold text-muted-foreground mb-2 block">
            <Calendar className="w-4 h-4 inline mr-1" /> Date
            {dayName && (
              <span className="normal-case" data-testid="date-day-of-week">
                : {dayName}
              </span>
            )}
          </Label>
          <Input
            id="date"
            data-testid="date-input"
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            min={minDate}
            max={maxDate}
            className="h-12 rounded-2xl"
          />
          <p className="text-xs text-muted-foreground mt-1">
            Bookings available up to 1 month in advance
          </p>
        </div>
      </div>

      {selectedLocation && selectedDate && (
        <div className="mt-4 p-4 bg-muted/50 rounded-xl">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-muted-foreground" />
              <span className="text-sm font-medium">Operating Hours:</span>
              <span className={`text-sm font-bold ${isLocationClosed ? 'text-red-600' : 'text-green-600'}`}>
                {getOperatingHoursDisplay()}
              </span>
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center gap-1.5 sm:gap-3 text-sm text-muted-foreground mt-1 sm:mt-0">
              {selectedLocation.email && (
                <a
                  href={`mailto:${selectedLocation.email}`}
                  className="inline-flex items-center gap-1 hover:text-primary transition-colors"
                  data-testid="branch-email-link"
                >
                  <Mail className="w-4 h-4" />
                  <span className="underline-offset-2 hover:underline">{selectedLocation.email}</span>
                </a>
              )}
              {selectedLocation.phone && (
                <span className="inline-flex items-center gap-1" data-testid="branch-phone">
                  <Phone className="w-4 h-4 text-pink-500" />
                  {selectedLocation.phone}
                </span>
              )}
              {(selectedLocation.instagram_url || DEFAULT_INSTAGRAM_URL) && (
                <a
                  href={selectedLocation.instagram_url || DEFAULT_INSTAGRAM_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 hover:text-pink-600 transition-colors"
                  data-testid="branch-instagram-link"
                  aria-label="Follow SmashZone on Instagram"
                  title="Follow us on Instagram"
                >
                  <Instagram className="w-4 h-4 text-pink-500" />
                  <span className="font-medium">@smashzone.bn</span>
                </a>
              )}
            </div>
          </div>
          {getBreakInfo && getBreakInfo.length > 0 && (
            <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm font-bold text-amber-800 flex items-center gap-2">
                🕌 Blocked Hours
              </p>
              {getBreakInfo.map((b, idx) => (
                <p key={`${b.start}-${b.end}-${idx}`} className="text-sm text-amber-700 mt-1">
                  {b.start} - {b.end}: {b.reason || 'Break'}
                </p>
              ))}
            </div>
          )}
        </div>
      )}
    </Card>
  );
}
